import { Formik } from 'formik';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import React, { useEffect, useState } from 'react';
import { Button, Col, FormGroup, Row } from 'react-bootstrap';
import * as Yup from 'yup';

import FormButtonGroup from 'App/components/styles/FormButtonGroup';
import CompanyService from 'App/services/CompanyService';
import ProtocolService from 'App/services/ProtocolService';
import { errorHandler } from 'App/utils';
import { CheckboxField } from 'Shared/form/CheckboxField';
import SelectField from 'Shared/form/SelectField';

const propTypes = {
    onHide: PropTypes.func.isRequired,
    excludeIds: PropTypes.arrayOf(PropTypes.number).isRequired,
    onSave: PropTypes.func.isRequired,
    show: PropTypes.bool,
};

const defaultProps = {
    show: false,
};

const getValuesAndSort = R.compose(R.sortBy(R.prop('name')), R.values);

const newOption = { name: 'Publish as a New Standalone', id: 'New' };

const ifNewOptionEquals = R.equals(newOption);

const validationSchema = Yup.object().shape({
    protocolToPublish: Yup.object().nullable().required('A protocol to publish is required.'),
    protocolToReplace: Yup.object().nullable().required('A protocol to replace is required.'),
});

function PublishProtocolModalBody({ onSave, onHide, show, excludeIds }) {
    //#region State
    const [availableCategories, setAvailableCategories] = useState([]);

    /**
     * Category is not stored in the form since we need an easy way to trigger a fetch when it changes.
     * What better way than a useEffect?
     */
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [availableProtocols, setAvailableProtocols] = useState([]);

    //#endregion

    //#region Side-Effects

    // Get available categories
    useEffect(() => {
        if (show) {
            CompanyService.getTherapyCategories()
                .then(getValuesAndSort)
                .then(setAvailableCategories)
                .catch(errorHandler);
        }
    }, [show]);

    // Get Available protocols
    const categoryId = R.prop('id', selectedCategory);

    useEffect(() => {
        if (categoryId) {
            ProtocolService.getProtocols(categoryId)
                .then(getValuesAndSort)
                .then(
                    //prettier-ignore
                    R.reject(
                        R.compose(
                            R.includes(R.__, excludeIds),
                            R.prop('id')
                        ),
                    )
                )
                .then(setAvailableProtocols)
                .catch(errorHandler);
        }
    }, [categoryId, excludeIds]);

    //#endregion

    return (
        <Formik
            initialValues={{
                protocolToPublish: null,
                protocolToReplace: null,
                enableForAll: false,
            }}
            onSubmit={(values) => {
                onSave(R.assoc('category', selectedCategory, values));
            }}
            validationSchema={validationSchema}
        >
            {({ handleSubmit, values, setValues, dirty, isValid }) => {
                return (
                    <form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={12}>
                                    <SelectField
                                        fieldName="category"
                                        options={availableCategories}
                                        isClearable
                                        label="Therapeutic Category"
                                        placeholder="Select"
                                        getOptionValue={R.prop('id')}
                                        getOptionLabel={R.prop('name')}
                                        onChange={(option) => {
                                            R.compose(
                                                R.thunkify(setSelectedCategory)(option),
                                                setValues,
                                                R.set(R.lensProp('protocolToPublish'), null),
                                                R.set(R.lensProp('protocolToReplace'), null)
                                            )(values);
                                        }}
                                        value={selectedCategory}
                                    />
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                    <SelectField
                                        fieldName="protocolToPublish"
                                        options={R.filter(R.prop('companyId'), availableProtocols)}
                                        label="Protocol to Publish"
                                        placeholder="Select"
                                        isClearable
                                        getOptionValue={R.prop('id')}
                                        getOptionLabel={R.prop('name')}
                                        onChange={(option) => {
                                            R.compose(
                                                setValues,
                                                R.set(R.lensProp('protocolToPublish'), option),
                                                R.set(R.lensProp('protocolToReplace'), null)
                                            )(values);
                                        }}
                                        value={values.protocolToPublish}
                                    />
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                    <SelectField
                                        fieldName="protocolToReplace"
                                        options={R.compose(
                                            R.prepend(newOption),
                                            R.reject(R.prop('companyId'))
                                        )(availableProtocols)}
                                        label="Protocol to Replace"
                                        placeholder="Select"
                                        getOptionValue={R.prop('id')}
                                        getOptionLabel={R.prop('name')}
                                        onChange={(option) => {
                                            R.compose(
                                                setValues,
                                                R.ifElse(
                                                    R.always(ifNewOptionEquals(option)),
                                                    R.set(R.lensProp('enableForAll'), true),
                                                    R.set(R.lensProp('enableForAll'), false)
                                                ),
                                                R.set(R.lensProp('protocolToReplace'), option)
                                            )(values);
                                        }}
                                        value={values.protocolToReplace}
                                    />
                            </Col>
                        </Row>

                        {ifNewOptionEquals(values.protocolToReplace) ? (
                            <Row>
                                <Col md={12}>
                                    <FormGroup>
                                        <CheckboxField fieldName="enableForAll" label="Enable for All" />
                                    </FormGroup>
                                </Col>
                            </Row>
                        ) : null}

                        <Row>
                            <Col md={12}>
                                <FormButtonGroup>
                                    <Button variant="outline-secondary" type="button" onClick={onHide}>
                                        Cancel
                                    </Button>
                                    <Button variant="primary" type="submit" disabled={dirty ? !isValid : true}>
                                        Confirm
                                    </Button>
                                </FormButtonGroup>
                            </Col>
                        </Row>
                    </form>
                );
            }}
        </Formik>
    );
}

PublishProtocolModalBody.propTypes = propTypes;
PublishProtocolModalBody.defaultProps = defaultProps;

export default PublishProtocolModalBody;
